import dayjs from "moment"
import { COLUMN_SYNONYMS, REQUIRED_FIELDS } from "./constants"
import type {
  AttendanceRecord,
  ColumnFieldDef,
  ColumnMap,
  DuplicateAction,
  UploadTimeSheetFailedEntity,
} from "./types"

export const getInitials = (name: string): string => {
  const parts = name
    .split(" ")
    .map((p) => p.trim())
    .filter(Boolean)
  if (parts.length === 0) return ""
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return parts[0].charAt(0).toUpperCase() + parts[1].charAt(0).toUpperCase()
}

export const formatDate = (iso: string | null | undefined) =>
  iso ? dayjs(iso).format("MMM DD YYYY") : "—"

export const formatTime = (iso: string | null | undefined) =>
  iso ? dayjs(iso).format("HH:mm:ss") : ""

export const countAffected = (entity: UploadTimeSheetFailedEntity): number =>
  entity.employeesAffected.reduce(
    (sum, group) => sum + (group.employees?.length ?? 0),
    0
  )

const synKeyFor = (stateKey: string) =>
  stateKey === "flags" ? "flag" : stateKey

const normalizeForMatch = (value: string) => {
  const firstLine = value.split(/\r?\n/)[0] ?? value
  return firstLine
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ")
}

export const matchesSynonyms = (
  stateKey: string,
  header: string | null | undefined
): boolean => {
  if (!header) return true
  const synonyms = COLUMN_SYNONYMS[synKeyFor(stateKey)]
  if (!synonyms || synonyms.length === 0) return true
  const h = normalizeForMatch(String(header))
  return synonyms.some((s) => {
    const sNorm = normalizeForMatch(s)
    return h === sNorm || h.includes(sNorm) || sNorm.includes(h)
  })
}

export { mapRowsToAttendanceRecords } from "./row-mapper"

export const isMapComplete = (columnMap: ColumnMap): boolean =>
  REQUIRED_FIELDS.every((f) => !!columnMap[f.stateKey])

export const computeInvalidMap = (
  columnMap: ColumnMap,
  fields: ColumnFieldDef[] = REQUIRED_FIELDS
): Record<string, string | null> =>
  fields.reduce((acc, f) => {
    const selected = columnMap[f.stateKey]
    acc[f.stateKey] =
      selected && !matchesSynonyms(f.stateKey, selected)
        ? `Selected column does not look like ${f.title}`
        : null
    return acc
  }, {} as Record<string, string | null>)

export const getOptionsFor = (
  headers: string[],
  columnMap: ColumnMap,
  stateKey: string
): string[] => {
  const usedHeaders = Object.values(columnMap).filter(Boolean) as string[]
  return headers.filter(
    (h) =>
      !usedHeaders.includes(h) ||
      columnMap[stateKey as keyof ColumnMap] === h
  )
}

interface RecordSignatureFields {
  employeeId: string | number
  date: string
  shift_period_id: number
  clockin_time: string
  clockout_time: string
}

const recordSignature = (r: RecordSignatureFields) =>
  `${String(r.employeeId)}|${r.date}|${r.shift_period_id}|${r.clockin_time}|${r.clockout_time}`

/**
 * Returns the records to send to `/upload`. Keeps successful records and
 * duplicates the user chose to overwrite; drops every other failed record
 * (rejected/unresolved duplicates and all non-duplicate failure types).
 */
export const computeRecordsForSubmission = (
  mappedRecords: AttendanceRecord[],
  failedUpload: UploadTimeSheetFailedEntity[],
  resolutions: Record<string, DuplicateAction>
): AttendanceRecord[] => {
  const allFailedSignatures = new Set<string>()
  const overwriteSignatures = new Set<string>()
  for (const failed of failedUpload) {
    for (const group of failed.employeesAffected) {
      for (const employee of group.employees) {
        if (!employee.record) continue
        const sig = recordSignature(employee.record)
        allFailedSignatures.add(sig)
        if (failed.type === "duplicate_entry") {
          const key = `${employee.employeeId}-${
            employee.affectedRecord?.id ?? "n/a"
          }`
          if (resolutions[key] === "overwrite") {
            overwriteSignatures.add(sig)
          }
        }
      }
    }
  }
  if (allFailedSignatures.size === 0) return mappedRecords
  return mappedRecords.filter((r) => {
    const sig = recordSignature(r)
    if (overwriteSignatures.has(sig)) return true
    return !allFailedSignatures.has(sig)
  })
}

/**
 * Walks the failed-upload response and returns the IDs of `affectedRecord`s
 * the user chose to overwrite via the duplicate-resolution drawer.
 */
export const computeRecordsToOverride = (
  failedUpload: UploadTimeSheetFailedEntity[],
  resolutions: Record<string, DuplicateAction>
): (number | string)[] => {
  const result: (number | string)[] = []
  for (const failed of failedUpload) {
    if (failed.type !== "duplicate_entry") continue
    for (const group of failed.employeesAffected) {
      for (const employee of group.employees) {
        const id = employee.affectedRecord?.id
        const key = `${employee.employeeId}-${
          employee.affectedRecord?.id ?? "n/a"
        }`
        if (id != null && resolutions[key] === "overwrite") {
          result.push(id)
        }
      }
    }
  }
  return result
}

export const buildDuplicateKey = (
  employeeId: number | string | null | undefined,
  affectedRecordId: number | string | null | undefined
) => `${employeeId ?? "n/a"}-${affectedRecordId ?? "n/a"}`

/**
 * Number of records that won't be imported on submit. A duplicate counts as
 * skipped unless the user picked "overwrite". All other failure types are
 * always skipped (they can't be resolved via the UI).
 */
export const countSkippedRecords = (
  failedUpload: UploadTimeSheetFailedEntity[],
  resolutions: Record<string, DuplicateAction>
): number => {
  let count = 0
  for (const failed of failedUpload) {
    for (const group of failed.employeesAffected) {
      for (const employee of group.employees) {
        if (failed.type === "duplicate_entry") {
          const key = buildDuplicateKey(
            employee.employeeId ?? employee.record?.employeeId,
            employee.affectedRecord?.id
          )
          if (resolutions[key] === "overwrite") continue
        }
        count++
      }
    }
  }
  return count
}
